import type React from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { lruMemoize } from '@reduxjs/toolkit';
import { debounce } from 'lodash-es';
import type { TreeNode } from 'tree-model';
import type { VListHandle } from 'virtua';

import type { AssetsCollectionSchemaWithId } from '@iconik-io/iconik-api/src/services/assets';
import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';
import { useVirtualizedScrollToIds } from '@iconik-io/shared-lib/src/react/hooks/use_virtualized_scroll_to_ids';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import type { UserRoles } from '@iconik-io/shared-lib/src/utils/roles';

import { isContextMenuEmpty } from '../../components/context_menu/utils';
import DraggableItems, {
  type DraggableItemsModifierKeys,
} from '../../components/draggable_items/draggable_items';
import { searchCollectionTree } from '../../components/modals/add_to_collection_advanced/thunks';
import { NoContentPanelCompact } from '../../components/no_content_panel_compact/no_content_panel_compact';
import SearchListDragImage from '../../components/search_items/search_items_drag_image/search_items_list_drag_image';
import { isParentMappedCollection } from '../../components/search_items/utils';
import SelectableItems from '../../components/selectable_items/selectable_items';
import { getTreeNodePathKey } from '../../components/tree/get_tree_node_path_key';
import { Tree } from '../../components/tree/tree';
import { TreeRow } from '../../components/tree/tree_row';
import { COLLECTIONS } from '../../constants/help_urls';
import {
  ADD_TO_COLLECTION_MODAL,
  NEW_COLLECTION_MODAL,
} from '../../constants/modals';
import { ID_COLLECTION } from '../../constants/reducer_ids';
import {
  COLLECTION_PAGE_ROUTE,
  SHARE_COLLECTION_PAGE_ROUTE,
} from '../../constants/routes';
import { SEARCH_DRAG } from '../../ducks/dragged_items/constants';
import type { MessageParams } from '../../ducks/messages/thunks';
import {
  type OpenModalPayload,
  openModal,
} from '../../ducks/modals_manager/slice';
import {
  setTreeSearchLoading,
  setTreeSearchQuery,
} from '../../ducks/tree/thunks';
import type { TreeReducerModel } from '../../ducks/tree/types';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import LoadingBar from '../../ui/loading_bar/loading_bar';
import {
  getParentCollectionsFromTree,
  isCollectionMappedToStorage,
  shouldShowCreateCollectionForm,
} from '../../utils/collections';
import { UUID_ZERO } from '../../utils/tree_handlers';
import SearchInputPanels from '../search_input_panels/search_input_panels';
import CollectionTreeContextMenu, { getDefinition } from './context_menu';

import styles from './collections.module.css';

export type NewCollectionFormData = {
  title: string;
};

type Props = {
  collection?: AssetsCollectionSchemaWithId;
  collectionId?: string;
  items: SearchItemEntity[];
  allDropTargetIds: string[];
  openModal: (payload: OpenModalPayload) => void;
  lockMappedCollections: boolean | null | undefined;
  onClick: (node: TreeNode<TreeReducerModel>) => void;
  onExpand: (node: TreeNode<TreeReducerModel>) => void;
  onLoadMore: (
    node: TreeNode<TreeReducerModel>,
    event: React.MouseEvent,
  ) => void;
  onResultItemsDrop: (
    dropItems: SearchItemEntity[],
    dropTarget: string,
    copyMode: boolean,
  ) => void;
  onScrollEnds?: () => void;
  readOnly: boolean;
  errorMessage: (message: MessageParams) => void;
  roles: UserRoles;
  route: string;
  selectedCollectionId?: string;
  tree: TreeNode<TreeReducerModel>[];
  loading: boolean;
  validCollection: (
    data: NewCollectionFormData,
    reset: (model?: NewCollectionFormData) => void,
  ) => void;
  write: boolean;
};

export const CollectionsPanelComponent = memo((props: Props) => {
  const dispatch = useAppDispatch();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [listDropTargetId, setListDropTargetId] = useState<string | undefined>(
    undefined,
  );

  const treeSearchQuery = useAppSelector(
    (state) => state.collectionsTree.treeSearchQuery,
  );
  const treeSearchLoading = useAppSelector(
    (state) => state.collectionsTree.treeSearchLoading,
  );
  const [searchText, setSearchText] = useState<string>(treeSearchQuery);

  // Save a list of dragged items that originate in our DraggableItems component
  // This is to know in onDrop if the drag originated from the panel or not
  const [isDraggingFromInsidePanel, setIsDraggingFromInsidePanel] =
    useState(false);

  const appState = useAppSelector((stateValue) => stateValue);

  const {
    allDropTargetIds,
    items,
    lockMappedCollections,
    onClick,
    onExpand,
    onLoadMore,
    onScrollEnds,
    readOnly,
    roles,
    selectedCollectionId,
    tree,
    write: canWriteCollections,
  } = props;

  const vListRef = useRef<VListHandle>(null);
  const { setScrollToIds } = useVirtualizedScrollToIds({
    virtualizer: vListRef.current,
    ids: items.map((item) => item.id),
  });

  useEffect(() => {
    if (!selectedCollectionId) {
      return;
    }
    setScrollToIds([selectedCollectionId]);
  }, [selectedCollectionId]);

  const onDrop = (
    dropItems: SearchItemEntity[],
    dropTargetId: string | null,
    modifierKeys: DraggableItemsModifierKeys,
  ): void => {
    const { collection } = props;

    if (
      dropTargetId === null ||
      dropItems.length === 0 ||
      !allDropTargetIds.includes(dropTargetId)
    ) {
      return;
    }
    const dropTargetItem = items.find((item) => item.id === dropTargetId);
    if (!dropTargetItem) {
      return;
    }

    const targetCollection = appState.collectionsTree.flatTree.find(
      (c) => c.model.id === dropTargetId,
    );

    // If we can't find the target collection in the tree then just bail
    if (!targetCollection) {
      return;
    }

    let copyOnly = readOnly;

    // The reason we check if we are on the collection page here is that we then need to allow move
    // If we are not then it's not clear where we are moving from and we only allow copy
    // But, if the drag originated within the panel then we don't care and will potentially allow move
    const isCollectionsRoute = COLLECTION_PAGE_ROUTE.includes(props.route);
    if (!isDraggingFromInsidePanel) {
      copyOnly ||= !isCollectionsRoute;
    }

    const isOnlyDraggingAssets = dropItems.every(
      ({ object_type }) => object_type === OBJECT_TYPE_ASSETS,
    );

    const dropTargetIsMappedCollection = getParentCollectionsFromTree(
      targetCollection,
    ).some((parent) => isCollectionMappedToStorage(parent));

    // Need to do some extra checks here if lockMappedCollections settings is enabled
    if (lockMappedCollections) {
      // We should never allow dropping on mapped collections if lockMappedCollections setting is enabled
      if (dropTargetIsMappedCollection) {
        props.errorMessage({ text: 'Cannot edit mapped collections' });
        return;
      }

      // Check if any of the items being dropped are mapped collections
      const dropItemsIncludeMappedCollection = dropItems.some((dropItem) =>
        isParentMappedCollection([dropItem], appState.collectionsTree.flatTree),
      );

      // Important to note here that collection will only be set if we are on a collections page.
      // Not if we are on the main search page. So check here if we are dragging assets from the collection page of a mapped collection
      const dropItemsIncludeAssetFromMapped =
        isOnlyDraggingAssets && isCollectionMappedToStorage(collection);

      // If any of the items being dropped are mapped collections or in a mapped collection
      // then we should only allow copying when lockMappedCollections is enabled
      if (dropItemsIncludeMappedCollection || dropItemsIncludeAssetFromMapped) {
        copyOnly = true;
      }
    }

    const copyMode = modifierKeys.altKey || copyOnly;

    // The reason we check isCollectionsRoute here is that if we are in a collection
    // we need to check if the user wants to move the assets or add
    // If we are on the search page then we can just add the assets
    const isMoveActionConfirmationRequired =
      !dropTargetIsMappedCollection &&
      isCollectionMappedToStorage(collection) &&
      lockMappedCollections
        ? false
        : isCollectionsRoute && !copyMode;

    if (
      isOnlyDraggingAssets &&
      !isMoveActionConfirmationRequired &&
      !dropTargetIsMappedCollection
    ) {
      props.onResultItemsDrop(dropItems, dropTargetId, copyMode);
    } else {
      props.openModal({
        id: ADD_TO_COLLECTION_MODAL,
        contextItems: [dropTargetItem, ...dropItems],
        options: {
          copyOnly,
          copyMode,
          isDraggingFromInsidePanel,
        },
      });
    }
  };

  const onSelectIds = (selectedIdsArg: string[]): void => {
    setSelectedIds(selectedIdsArg);
  };

  const getSelectedIds = lruMemoize(
    (selectedCollectionIdArg: string | undefined) =>
      selectedCollectionIdArg ? [selectedCollectionIdArg] : [],
  );

  const getSelectedItems = lruMemoize(
    (itemsArr: SearchItemEntity[], selectedIdsArg: string[]) =>
      itemsArr.filter((item) => selectedIdsArg.includes(item.id)),
  );
  const isSearchResults = Boolean(searchText);

  const treeElements = tree.map((node, index) => {
    const selectedIdsInRowRenderer = getSelectedIds(selectedCollectionId);
    const selectedItem = getSelectedItems(items, [node.model.id]);
    const contextMenuEmpty =
      index !== 0
        ? isContextMenuEmpty(getDefinition, appState, selectedItem)
        : false;
    const isSearchResultsRootItem =
      isSearchResults && node.parent?.model.id === UUID_ZERO;

    return (
      <TreeRow
        key={getTreeNodePathKey(node)}
        node={node}
        withMenu={
          // hide for now the menu for shares
          // enable when the agent can download from shares
          // just remove this prop
          !contextMenuEmpty && tree.length > 0 && tree[0].model.id === UUID_ZERO
        }
        onLoadMore={onLoadMore}
        onExpand={onExpand}
        onClick={onClick}
        dropTarget={listDropTargetId}
        selectedIds={selectedIdsInRowRenderer}
        isSearchResults={isSearchResults}
        shouldHaveBreadcrumbs={isSearchResultsRootItem}
      />
    );
  });

  const showNewButton = shouldShowCreateCollectionForm(
    selectedCollectionId,
    canWriteCollections,
    roles.canCreateCollections,
    roles.canCreateRootCollections,
  );

  const selectedItems = getSelectedItems(items, selectedIds);

  const enableDragAndDrop = canWriteCollections;

  const hasNonPreLoadedContent = () => {
    if (tree.length === 0) {
      return false;
    }
    const [root, ...children] = tree;
    if (root.model.id !== UUID_ZERO) {
      return true;
    }
    return !children.every((child: TreeNode) =>
      child.model?.metadata?.iconikTutorialContent?.includes(true),
    );
  };

  const selectableItemsElement = treeSearchLoading ? (
    <LoadingBar />
  ) : (
    <>
      <SelectableItems
        multiselect={false}
        items={items}
        contextMenu={
          // hide for now the menu for shares
          // enable when the agent can download from shares
          // just remove this prop
          tree.length && tree[0].model.id === UUID_ZERO
            ? CollectionTreeContextMenu
            : undefined
        }
        onSelectIds={onSelectIds}
        isSelectAllAllowed={false}
        className={styles.tree}
      >
        <Tree
          ref={vListRef}
          items={treeElements}
          onScrollEnds={onScrollEnds}
          itemSize={28}
          aria={{ label: 'Collections tree' }}
        />
      </SelectableItems>
      {!hasNonPreLoadedContent() && !props.loading ? (
        <NoContentPanelCompact
          title="Use collections to easily organize, share and manage access to content"
          actionText="CREATE COLLECTION"
          linkHref={COLLECTIONS}
          linkText="Learn more"
          onActionClick={() => {
            dispatch(
              openModal({
                id: NEW_COLLECTION_MODAL,
                contextItems: [],
              }),
            );
          }}
        />
      ) : null}
    </>
  );

  const handleSearchTextInput = useCallback(
    debounce(async (value: string) => {
      dispatch(setTreeSearchLoading(ID_COLLECTION, true));
      setSearchText(value);
      dispatch(setTreeSearchQuery(ID_COLLECTION, value));
      await dispatch(searchCollectionTree(value, ID_COLLECTION));
      dispatch(setTreeSearchLoading(ID_COLLECTION, false));
    }, 400),
    [],
  );

  const handleNewButtonClick = () => {
    dispatch(
      openModal({
        id: NEW_COLLECTION_MODAL,
        contextItems:
          selectedCollectionId && props.collection
            ? [props.collection]
            : undefined,
      }),
    );
  };
  const isShareRoute = props.route === SHARE_COLLECTION_PAGE_ROUTE;
  return (
    <div className={styles.root}>
      {!isShareRoute && (
        <SearchInputPanels
          searchText={searchText}
          showNewButton={showNewButton}
          isNewButtonDisabled={readOnly}
          onTextInput={handleSearchTextInput}
          onNewButtonClick={handleNewButtonClick}
        />
      )}
      {enableDragAndDrop ? (
        <DraggableItems
          className={styles.tree_wrapper}
          name={SEARCH_DRAG}
          dragImageComponent={SearchListDragImage}
          onDragStart={() => setIsDraggingFromInsidePanel(true)}
          onDrop={onDrop}
          onDragEnd={() => setIsDraggingFromInsidePanel(false)}
          selectedItems={selectedItems}
          allDropTargetIds={allDropTargetIds}
          setListDropTargetId={setListDropTargetId}
        >
          {selectableItemsElement}
        </DraggableItems>
      ) : (
        <div className={styles.tree_wrapper}>{selectableItemsElement}</div>
      )}
    </div>
  );
});
